import { LumiarToastContainer, toastOptions } from '@legacy/common/_components/LumiarToast'
import Logo from '@legacy/common/components/shared/Logo'
import { SchoolLogoColor } from '@legacy/common/types/whitelabel'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import Cookies from 'js-cookie'
import { useTranslation } from 'react-i18next'

import DeviseLanguage from '@legacy/common/components/DeviseLanguage/deviseLanguage'
import {
  Anchor,
  Button,
  Checkbox,
  Container,
  Divider,
  Group,
  Paper,
  PasswordInput,
  Space,
  Text,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { GoogleButton } from './session-new-google-button-component'
import { useSessionNew } from './session-new-hook'
import { Form, Mosaico, PaperStyled } from './session-new-styles'

import { Heading } from 'components'
import { Flex } from 'components/Flex'
import { useNotification } from 'context/useNotificationContext'
import { useSpecificFeature } from 'hooks'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { EmailConfirmedModal } from 'pages/TeacherSignup/components/EmailConfirmedModal'

export interface ISession {
  readonly children?: never
}

const SessionNew = (props: ISession) => {
  const { t, i18n } = useTranslation()
  const sessionData = useSessionNew()
  const { payload, loading, change, check, logIn } = sessionData || {}
  const [visible, { toggle }] = useDisclosure(false)
  const [searchParams] = useSearchParams()
  const [urlToRedirect, setUrlToRedirect] = useState('')
  const isMobile = useMediaQuery('(max-width: 600px)')
  const { handleNotification } = useNotification()
  const { enabled: isArcSignupEnabled, loading: isFeatureLoading } = useSpecificFeature('arc-mosaico-project')
  const { enabled: isSignupButtonEnabled } = useSpecificFeature('arc-mosaico-signup')
  const [showEmailConfirmedModal, setShowEmailConfirmedModal] = useState(false)

  const handleSubmit = (values: typeof form.values) => {
    if (payload && logIn) {
      logIn(values.email, values.password)
    }
  }

  const form = useForm({
    initialValues: { email: payload?.login || '', password: payload?.password || '' },
  })

  useEffect(() => {
    const cookieUrlToRedirect: string | undefined | null = Cookies.get('url_to_redirect')
    if (cookieUrlToRedirect !== '' && cookieUrlToRedirect !== undefined && cookieUrlToRedirect !== null) {
      setUrlToRedirect(cookieUrlToRedirect)
    }
  }, [urlToRedirect])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const paramType: string | null = searchParams.get('success')

    switch (paramType) {
      case 'change_sucess':
        handleNotification(true, t('forgot_password.change_sucess'), true, 'success')
        break
      case 'not_changed':
        handleNotification(true, t('forgot_password.not_changed'), true, 'error')
        break
      default:
        break
    }
  }, [])

  useEffect(() => {
    const sendResetPasswordInstructions = searchParams.get('send_reset_password_instructions')
    if (sendResetPasswordInstructions === 'true') toast.success(t('send_instructions'), toastOptions)
  }, [])

  useEffect(() => {
    const emailConfirmed = searchParams.get('email_confirmed')
    if (emailConfirmed === 'true') {
      setShowEmailConfirmedModal(true)
      // Clean query param from URL
      const url = new URL(window.location.href)
      url.searchParams.delete('email_confirmed')
      window.history.replaceState({}, '', url.toString())
    }
  }, [])

  // Arc: forçar idioma inglês
  useEffect(() => {
    if (isArcSignupEnabled) {
      i18n.changeLanguage('en')
    }
  }, [isArcSignupEnabled, i18n])

  return (
    <>
      <Mosaico>
        <Logo color={SchoolLogoColor.White} />
        {!isArcSignupEnabled && <DeviseLanguage />}
      </Mosaico>
      <div>
        <LumiarToastContainer />
        <Space h='xl' />
        <Flex align='center' direction='column' justify='center'>
          <Container px={isMobile ? 20 : 0} size={isMobile ? 'auto' : '52rem'}>
            <Heading size='xl' text={t('login')} />
          </Container>
          <PaperStyled>
            <Paper shadow='md' mt={20} radius='md' p='xl'>
              <Form onSubmit={form.onSubmit(handleSubmit)}>
                <TextInput
                  id='input-email'
                  size='md'
                  aria-label={t('account_signIn_placeholder_login')}
                  label={t('account_signIn_placeholder_login')}
                  name='login'
                  disabled={loading}
                  {...form.getInputProps('email')}
                />
                <PasswordInput
                  mt='md'
                  id='input-password'
                  size='md'
                  label={t('account_signIn_placeholder_password')}
                  aria-label={t('account_signIn_placeholder_password')}
                  name='password'
                  disabled={loading}
                  visible={visible}
                  onVisibilityChange={toggle}
                  {...form.getInputProps('password')}
                />
                <Group mt='md' position={isMobile ? 'center' : 'apart'}>
                  <Checkbox
                    label={t('account_signIn_remember')}
                    aria-label={t('account_signIn_remember')}
                    labelPosition='right'
                    onChange={check}
                    id='rememberMe'
                    name='rememberMe'
                    size='md'
                    checked={payload?.rememberMe || false}
                    disabled={loading}
                  />
                  <Anchor href='/forgot-password' aria-label={t('account_signIn_forgot_password')}>
                    {t('account_signIn_forgot_password')}
                  </Anchor>
                </Group>

                <Button
                  fullWidth
                  mt='md'
                  variant='filled'
                  disabled={loading}
                  type='submit'
                  size='lg'
                  aria-label={t('account_signIn_login')}
                >
                  {t('account_signIn_login')}
                </Button>
              </Form>
              <Divider my='lg' label={t('account_signIn_divider')} labelPosition='center' />
              <GoogleButton disabled={loading} />

              {isSignupButtonEnabled && (
                <>
                  <Space h='xl' />
                  <Text align='center' color='dimmed' size='sm'>
                    {t('teacher_signup.no_account')}
                  </Text>
                  <Button
                    component='a'
                    href='/account/teacher-signup'
                    fullWidth
                    mt='md'
                    variant='outline'
                    disabled={loading}
                    size='lg'
                  >
                    {t('teacher_signup.sign_up_now')}
                  </Button>
                </>
              )}
            </Paper>
          </PaperStyled>
        </Flex>
      </div>

      <EmailConfirmedModal
        opened={showEmailConfirmedModal}
        onClose={() => setShowEmailConfirmedModal(false)}
      />
    </>
  )
}

export default SessionNew
